import * as React from 'react';
import { useMemo } from 'react';
import type { ClusterKind, DayRateSnapshot, RateSnapshotData } from './types';
import { getClusterCurrentDays } from './clusterUtils';
import { DayRatePriceCell } from './DayRatePriceCell';
import { RemoveDropdownButton } from './RemoveDropdownButton';

interface Props {
    cluster: {
        id: string;
        kind: ClusterKind;
        displayName: string;
        startDate: string;
        endDate: string;
    };
    allDates: string[];
    selectedDates: Set<string>;
    onSelectedDatesChange: (next: Set<string>) => void;
    targetCurrent: RateSnapshotData | null;
    memberIds: string[];
    onClose: () => void;
    formatCurrency: (amount: number) => string;
}

export function CreateClusterEditorPanel({
    cluster, allDates, selectedDates, onSelectedDatesChange,
    targetCurrent, memberIds, onClose, formatCurrency,
}: Props) {
    const currentDayByDate = useMemo(() => {
        const m = new Map<string, DayRateSnapshot>();
        const days = getClusterCurrentDays(memberIds, cluster.kind, targetCurrent);
        for (const day of days) {
            if (!m.has(day.date)) m.set(day.date, day);
        }
        return m;
    }, [targetCurrent, cluster.kind, memberIds]);

    function removeJustThisDate(date: string) {
        const next = new Set(selectedDates);
        next.delete(date);
        onSelectedDatesChange(next);
    }

    function removeAndAbove(date: string) {
        const idx = allDates.indexOf(date);
        if (idx < 0) return;
        const next = new Set<string>();
        for (let i = 0; i < idx; i++) {
            if (selectedDates.has(allDates[i])) next.add(allDates[i]);
        }
        onSelectedDatesChange(next);
    }

    function removeAndBelow(date: string) {
        const idx = allDates.indexOf(date);
        if (idx < 0) return;
        const next = new Set<string>();
        for (let i = idx + 1; i < allDates.length; i++) {
            if (selectedDates.has(allDates[i])) next.add(allDates[i]);
        }
        onSelectedDatesChange(next);
    }

    function restoreAll() {
        onSelectedDatesChange(new Set(allDates));
    }

    function restoreDate(date: string) {
        const next = new Set(selectedDates);
        next.add(date);
        onSelectedDatesChange(next);
    }

    const removedCount = allDates.length - selectedDates.size;

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h6 className="font-semibold mb-0">Editing: {cluster.displayName}</h6>
                    <div className="text-xs text-gray-500">{cluster.startDate} — {cluster.endDate}</div>
                </div>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
                    Done
                </button>
            </div>

            <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-600">
                    All dates included by default. Use <em>Remove</em> to exclude individual dates.
                </span>
                {removedCount > 0 && (
                    <button type="button" className="btn btn-sm btn-link p-0" onClick={restoreAll}>
                        Restore all
                    </button>
                )}
            </div>

            <table className="table table-sm text-xs w-full" style={{ tableLayout: 'fixed' }}>
                <thead>
                    <tr>
                        <th style={{ width: '110px' }}>Date</th>
                        <th>Current price</th>
                        <th className="text-right" style={{ width: '180px' }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {allDates.map(date => {
                        const isIncluded = selectedDates.has(date);
                        const currentDay = currentDayByDate.get(date) ?? null;
                        return (
                            <tr key={date} className={isIncluded ? '' : 'text-gray-400'}>
                                <td className="align-top">{date}</td>
                                <td className="align-top font-mono">
                                    {currentDay
                                        ? <DayRatePriceCell dayRate={currentDay} formatCurrency={formatCurrency} />
                                        : <span className="text-gray-400">—</span>}
                                </td>
                                <td className="text-right align-top">
                                    {isIncluded ? (
                                        <RemoveDropdownButton
                                            onRemove={() => removeJustThisDate(date)}
                                            onRemoveAndAbove={() => removeAndAbove(date)}
                                            onRemoveAndBelow={() => removeAndBelow(date)}
                                        />
                                    ) : (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => restoreDate(date)}
                                        >
                                            Restore
                                        </button>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
